import React, { useState } from "react";

import * as Cesium from "cesium";

import { Dialog, CloseButton, Button, Text, HStack, Field, Input, SimpleGrid, RadioGroup, Portal } from "@chakra-ui/react";
import { checkZigzagOnDataSource } from "../../../../../utilies/geojson/CheckZigzag";
import { Rectangle } from "cesium";
import { LuFocus } from "react-icons/lu";
import { blinkEntity } from "../../../../../utilies/EntityUtility";
import { useAppStore } from "../../../../../context/store";
const FindBreakPoints = () => {
  // state
  const showZigzagDialog = useAppStore((s) => s.showZigzagDialog);

  // setter / ref
  const setShowZigzagDialog = useAppStore.getState().setShowZigzagDialog;
  const viewerRef = useAppStore.getState().viewerRef;

  const items = [
    { label: "On each line", value: "1" },
    { label: "Between two lines", value: "2" },
  ];
  const [mode, setMode] = useState(1);

  const [zigzagAngle, setZigzagAngle] = useState(45);
  const [result, setResult] = useState([]);

  const filter = () => {
    const dataSource = showZigzagDialog?.data?.dataSource;
    if (!dataSource) return;
    resetSlopeFilter();
    checkZigzagOnDataSource(
      dataSource,
      Number(zigzagAngle),
      false,
      (zigzags) => {
        if (!zigzags.length || !viewerRef?.current) return;
        setResult(zigzags);
      },
      mode == 1 ? false : true,
    );
  };

  const resetSlopeFilter = () => {
    const dataSource = showZigzagDialog?.data?.dataSource;
    if (!dataSource) return;
    checkZigzagOnDataSource(
      dataSource,
      Number(zigzagAngle),
      false,
      (zigzags) => {
        if (!zigzags.length || !viewerRef?.current) return;
        setResult(zigzags);
      },
      mode == 1 ? false : true,
    );

    setResult([]);
  };

  return (
    <Dialog.Root modal={false} open={showZigzagDialog?.open ? true : false} closeOnInteractOutside={false}>
      <Portal>
        <Dialog.Positioner style={{ pointerEvents: "none" }}>
          <Dialog.Content style={{ pointerEvents: "auto" }}>
            <Dialog.Header>
              <Dialog.Title>Find Break Points</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <RadioGroup.Root
                defaultValue="1"
                mb={3}
                onValueChange={(v) => {
                  setMode(parseInt(v.value));
                }}
              >
                <HStack gap="6">
                  {items.map((item) => (
                    <RadioGroup.Item key={item.value} value={item.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </HStack>
              </RadioGroup.Root>
              <HStack gap={3} mb={3}>
                <Field.Root>
                  <Field.Label>Angle (degree)</Field.Label>
                  <Input
                    placeholder="Enter angle"
                    value={zigzagAngle}
                    type="number"
                    onChange={(v) => {
                      setZigzagAngle(v.target.value);
                    }}
                  />
                </Field.Root>
              </HStack>

              {result.length > 0 && <Text>Results: {result.length}</Text>}

              <SimpleGrid columns={3} gap={1} maxHeight={"120px"} overflowY={"scroll"}>
                {[...result]
                  .sort((a, b) => {
                    const aDeg = parseFloat(a.deviationAngleDeg) || 0;
                    const bDeg = parseFloat(b.deviationAngleDeg) || 0;
                    return bDeg - aDeg;
                  })
                  .map((res, index) => {
                    const isBetween = res.mode === "betweenEntities";

                    const mainEntity = isBetween ? res.entityA || res.entityB : res.entity;

                    let label = "zigzag " + (index + 1);

                    const props = mainEntity && mainEntity.properties;
                    if (props && props.id) {
                      const now = Cesium.JulianDate.now();
                      const idValue = props.id.getValue ? props.id.getValue(now) : props.id;
                      if (idValue != null) {
                        label = idValue;
                      }
                    }

                    return (
                      <HStack key={index} justify="space-between" bg="blackAlpha.200" px={2} py={0} borderRadius="md">
                        <Text>
                          {label} {parseFloat(res.deviationAngleDeg) != null ? parseFloat(res.deviationAngleDeg).toFixed(2) : ""}°
                        </Text>
                        <Button
                          outline="none"
                          bg="transparent"
                          onClick={() => {
                            const viewer = viewerRef.current;
                            if (!viewer) return;

                            let { west, south, east, north } = res;

                            const paddingFactor = 0.3;
                            const width = east - west;
                            const height = north - south;

                            west -= width * paddingFactor;
                            east += width * paddingFactor;
                            south -= height * paddingFactor;
                            north += height * paddingFactor;

                            const rect = Rectangle.fromDegrees(west, south, east, north);

                            viewer.camera.flyTo({
                              destination: rect,
                              duration: 1.5,
                              complete: () => {
                                const targets = isBetween ? [res.entityA, res.entityB].filter(Boolean) : [mainEntity].filter(Boolean);

                                targets.forEach((ent) => {
                                  blinkEntity(viewer, ent, {
                                    zoom: false,
                                    duration: 4000,
                                    step: 200,
                                  });
                                });
                              },
                            });
                          }}
                        >
                          <LuFocus color="MenuText" />
                        </Button>
                      </HStack>
                    );
                  })}
              </SimpleGrid>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowZigzagDialog(null);
                  }}
                >
                  Cancel
                </Button>
              </Dialog.ActionTrigger>

              <Button
                colorPalette={"red"}
                onClick={() => {
                  resetSlopeFilter();
                }}
              >
                Reset
              </Button>
              <Button
                onClick={() => {
                  filter();
                }}
              >
                Filter
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger
              asChild
              onClick={() => {
                setShowZigzagDialog(null);
              }}
            >
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default FindBreakPoints;
